import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, share } from 'rxjs/operators';
import { ErrorService } from '@services/error/error.service';
import { CookieService } from 'ngx-cookie';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { Router } from '@angular/router';
import { ModalService } from '@services/modal/modal.service';
import { AccountTypeEnum } from '@shared/enums/account-type.enum';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private env = environment;

  constructor(
    private http: HttpClient,
    private errorService: ErrorService,
    private cookie: CookieService,
    private localize: LocalizeRouterService,
    private router: Router,
    private modal: ModalService
  ) {}


  public login(email: string, pass: string): Observable<{ code: string, user: number }> {
    const sub$ = this.http.post<{ code: string, user: number }>('auth/login', {email, pass})
      .pipe(share());

    sub$.subscribe({
      error: ((err: HttpErrorResponse) => {
          this.errorService.showError(err);
        }
      )
    });

    return sub$;
  }

  public signUp(data: { email: string, password: string, confirmPassword: string, companyType: AccountTypeEnum }): Observable<any> {
    const sub$ = this.http.post('auth/signup', {...data}, {responseType: 'text'}).pipe(share());

    sub$.subscribe({
      error: ((err: HttpErrorResponse) => {
          this.errorService.showError(JSON.parse(err.error).message);
        }
      )
    });

    return sub$;
  }

  public resendActivationEmail(email: string): Observable<any> {
    const sub$ = this.http.post('auth/resend-activation-email', {email}, {responseType: 'text'}).pipe(share());

    sub$.subscribe({
      error: ((err: HttpErrorResponse) => {
          this.errorService.showError(err);
        }
      )
    });

    return sub$;
  }

  public saveToken(token: string): void {
    this.cookie.put('DWallUserToken', token);
    this.cookie.put('DWallIsLogged', 'true');
  }

  public saveAccType(accountType: AccountTypeEnum): void {
    this.cookie.put('DWallAccountType', accountType);

    if (accountType === AccountTypeEnum.advertiser) this.redirectToAdvertiserModule();
    else this.redirectToTvOwnerModule();
  }

  redirectToAdvertiserModule(): void {
    const path = this.localize.translateRoute('/advertiser/devices');
    this.router.navigate([path]);
  }

  redirectToTvOwnerModule(): void {
    const path = this.localize.translateRoute('/app/devices/all');
    this.router.navigate([path]);
  }

  public logout(): void {
    this.clearLocalStorageDWallData();
    if (this.env.theme !== 'default') {
      const domainUrl = window.location.origin;
      window.location.href = `${domainUrl}`;
      return;
    }
    window.location.href = this.landingUrl;
  }

  get landingUrl(): string {
    const lang = this.localize.translateRoute('/');
    return this.env.landingUrl + lang;
  }

  public verify(code: string, user: number): Observable<{token: string}> {
    return this.http.post<{token: string}>('auth/verify', {user, code}).pipe(
      catchError(err => {
        this.errorService.showError(err);
        return throwError(err);
      })
    );
  }

  public clearLocalStorageDWallData(): void {
    this.cookie.remove('DWallIsLogged');
    this.cookie.remove('DWallUserToken');
    this.cookie.remove('DWallAccountType');
    localStorage.removeItem('DwallRole');
    localStorage.removeItem('DWallMarketplaceMode');
  }

  public resetPassword(email: string, resetPasswordPageUrl: string): Observable<any> {
    const observable: Observable<any> = this.http.put('auth/resetPass', {email, resetPasswordPageUrl})
      .pipe(
        share(),
        catchError((err: HttpErrorResponse) => {
          this.errorService.showError(err);
          return throwError(err);
        })
      );

    observable.subscribe(
      ((msg: {message: string}) => this.modal.showMsg(msg?.message)),
    );

    return observable;
  }

  public completeResetPassword(data: {password: string, confirm: string, token: string, email: string}): Observable<any> {
    const headers = new HttpHeaders({authorization: `Bearer ${data.token}`});
    return this.http.post('auth/completeResetPass',
      { newPass: data.password , email: data.email}, {headers})
      .pipe(
        catchError((err: HttpErrorResponse) => {
          this.errorService.showError(err);
          return throwError(err);
        })
      );
  }
}
